import { message } from '@pankod/refine-antd'
import { setCandidateReportVisibility } from 'gql/mutations'
import { getStoredScoreCardPdfLinks } from 'gql/query'
import { deleteReportCardLinks } from 'helpers/evaluations/reportCardeDeletion'
import { generateReportCardLink } from 'helpers/scorecardUrls'
import { candidateIdVariants, isSameCandidate } from 'utility/candidateId'

const LINK_BATCH_SIZE = 10

// Old scorecards are public DigitalOcean PDFs whose file key cannot be rebuilt from the candidate id. Hiding clears
// score_card_url (the DB trigger does it on the same update), so delete those files first while the URL is still known.
const deleteStoredScoreCardPdfs = async (candidateIds: string[]) => {
  try {
    const pdfLinks = await getStoredScoreCardPdfLinks(candidateIds)
    if (pdfLinks.length === 0) {
      return
    }
    const response = await deleteReportCardLinks(pdfLinks.map(row => ({ candidateId: row.candidate_id, url: row.score_card_url })))
    if (!response.status) {
      console.warn('Scorecard PDFs left in storage after hiding:', pdfLinks)
      message.warning(`${pdfLinks.length} old scorecard PDF(s) could not be deleted and stay downloadable; the links are in the browser console.`)
    }
  } catch (err) {
    console.error(err, 'while deleting stored scorecard PDFs')
    message.warning('Could not check for old scorecard PDFs to delete; the scorecard is still hidden.')
  }
}

// Creates the shareable scorecard link for results that were just made visible and returns the ids that failed.
// A failed link is still created the next time the reportcard-link API is called for that candidate.
const generateScoreCardLinks = async (candidateIds: string[]) => {
  const failedIds: string[] = []
  for (let i = 0; i < candidateIds.length; i += LINK_BATCH_SIZE) {
    const batch = candidateIds.slice(i, i + LINK_BATCH_SIZE)
    const results = await Promise.allSettled(batch.map(candidateId => generateReportCardLink(candidateId)))
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.warn(`Scorecard link not generated for ${batch[index]}:`, result.reason?.message || result.reason)
        failedIds.push(batch[index])
      }
    })
  }
  return failedIds
}

export const updateReportCardVisibility = async (candidateDetails: any[]) => {
  try {
    const hasHideVisibilityIds: any[] = []
    const hasProvideVisibilityIds: any[] = []

    for (const candidate of candidateDetails) {
      if (candidate?.visibility) {
        hasHideVisibilityIds.push(...candidateIdVariants(candidate?.candidate_id))
      } else {
        hasProvideVisibilityIds.push(...candidateIdVariants(candidate?.candidate_id))
      }
    }
    if (hasHideVisibilityIds && hasHideVisibilityIds.length > 0) {
      await deleteStoredScoreCardPdfs(hasHideVisibilityIds)
      await setCandidateReportVisibility(hasHideVisibilityIds, false)
    }
    if (hasProvideVisibilityIds && hasProvideVisibilityIds.length > 0) {
      const visibleCandidateIds = await setCandidateReportVisibility(hasProvideVisibilityIds, true)
      const failedIds = await generateScoreCardLinks(Array.from(new Set(visibleCandidateIds)))
      if (failedIds.length > 0) {
        message.warning(`Scorecard link not created for ${failedIds.length} candidate(s) (see browser console); it is created when the link is next requested.`)
      }
    }
    return true
  } catch (err) {
    return null
  }
}

export const changeBulkReportCardVisibility = async (selectedDetails: any[], allCandidateDetails: any[]) => {
  console.log(selectedDetails, 'candidateUuids', allCandidateDetails, 'Candidate details')
  const selectedCandidateIdsWithVisibility = selectedDetails
    .map(({ application_id, id }) => {
      const candidateDetails = allCandidateDetails.find(reportCardDetails => isSameCandidate(reportCardDetails?.candidate_id, application_id))
      if (candidateDetails) {
        return { candidate_id: application_id, visibility: candidateDetails?.is_visible }
      }
    })
    .filter(Boolean)
  if (selectedCandidateIdsWithVisibility && selectedCandidateIdsWithVisibility?.length > 0) {
    await updateReportCardVisibility(selectedCandidateIdsWithVisibility)
  } else {
    return false
  }
}
